import React from "react"
import {EntryDate, EntrySelector} from "../../../components/modal"

/**
 * Preset semester name options for the name selector.
 * @type {Array<{label: string, value: string}>}
 */
export const SEMESTER_NAME_OPTIONS = [
  {label: "Fall", value: "Fall"},
  {label: "Winter", value: "Winter"},
  {label: "Spring", value: "Spring"},
  {label: "Summer", value: "Summer"},
]

/**
 * Get a selector-compatible option object from semester data.
 * @param {Object|null} data - Semester data object
 * @returns {{label: string, value: string}|null} Option object or null
 */
const getName = (data) => {
  if (!data || !data.name) return null
  return {label: data.name, value: data.name}
}

const _getDate = (data, key) => {
  if (!data || !data[key]) return null
  return data[key]
}

/**
 * Extract start_date from semester data.
 * @param {Object|null} data - Semester data object
 * @returns {string|null} Start date string or null
 */
const getStartDate = (data) => _getDate(data, "start_date")

/**
 * Extract end_date from semester data.
 * @param {Object|null} data - Semester data object
 * @returns {string|null} End date string or null
 */
const getEndDate = (data) => _getDate(data, "end_date")

/**
 * Validate a date string in YYYY-MM-DD format.
 * @param {string|null} date - Date string to validate
 * @returns {boolean} Whether the date is valid
 */
export const isValidDate = date => {
  if (!date) return false
  const parts = date.split('-')
  if (parts.length !== 3) return false
  const [y, m, d] = parts.map(Number)
  const asDate = new Date(Date.UTC(y, m - 1, d))
  return asDate.getUTCFullYear() === y &&
         asDate.getUTCMonth() === m - 1 &&
         asDate.getUTCDate() === d
}

/**
 * Return a YYYY-MM-DD string offset by `days` from the given date string.
 * @param {string} dateStr - YYYY-MM-DD date string
 * @param {number} days - Number of days to offset (positive = future, negative = past)
 * @returns {string} YYYY-MM-DD date string
 */
const nextDay = (dateStr, days) => {
  const [y, m, d] = dateStr.split('-').map(Number)
  const date = new Date(Date.UTC(y, m - 1, d + days))
  return date.toISOString().split('T')[0]
}

/**
 * @typedef {Object} SemesterFormData
 * @property {string|null} name - Semester name
 * @property {string|null} start_date - Start date (YYYY-MM-DD)
 * @property {string|null} end_date - End date (YYYY-MM-DD)
 */

/**
 * Reusable semester form fields (name selector, start/end dates).
 * Used by both SemesterModal and Create Institution modal.
 *
 * @param {Object} props
 * @param {SemesterFormData} props.data - Current semester form data
 * @param {(updates: Partial<SemesterFormData>) => void} props.onChange - Called with changed fields
 * @param {Object|null} [props.existingSemester] - Existing semester for edit mode (adds its name to options if custom)
 * @param {string|null} [props.dateRowClassName] - Optional CSS class for the date row container
 */
export function SemesterFormFields({data, onChange, existingSemester = null, dateRowClassName = null}) {
  const name = getName(data)
  const startDate = getStartDate(data)
  const endDate = getEndDate(data)

  let options = SEMESTER_NAME_OPTIONS
  if (existingSemester != null && !options.some(o => o.value === existingSemester.name)) {
    options = [
      ...options,
      getName(existingSemester),
    ]
  }

  const dateFields = (
    <>
      <EntryDate
        label="Start Date"
        value={startDate}
        max={isValidDate(endDate) ? nextDay(endDate, -1) : undefined}
        onChange={(newValue) => {
          if (newValue != null && !newValue.length) {
            newValue = null
          }

          if (data.start_date === newValue) {
            return
          }

          let updatedName = data.name
          const hasName = !!data.name && data.name.length > 0

          if (!hasName) {
            onChange({start_date: newValue})
            return
          }

          const nameOption = SEMESTER_NAME_OPTIONS.filter(o => updatedName.toLowerCase().includes(o.value.toLowerCase()))
          const isCustomName = !nameOption.length

          if (!isCustomName && isValidDate(newValue)) {
            const date = new Date(newValue)
            if (!data.name.startsWith(String(date.getUTCFullYear()))) {
              updatedName = `${date.getUTCFullYear()} ${nameOption[0].value}`
            }
          }

          const updates = {start_date: newValue}
          if (updatedName !== data.name) {
            updates.name = updatedName
          }

          onChange(updates)
        }}
      />
      <EntryDate
        label="End Date"
        value={endDate}
        min={isValidDate(startDate) ? nextDay(startDate, 1) : undefined}
        onChange={(newValue) => {
          if (newValue != null && !newValue.length) {
            newValue = null
          }

          if (data.end_date === newValue) {
            return
          }

          onChange({end_date: newValue})
        }}
      />
    </>
  )

  return (
    <>
      <EntrySelector
        creatable
        isClearable
        label="Semester"
        placeholder="Semester Name"
        value={name}
        options={options}
        menuPortalTarget={document.body}
        onChange={(newValue) => {
          const isCustom = !!newValue && !!newValue.__isNew__

          let selectedName = !!newValue && !!newValue.value ? newValue.value : null
          if (!isCustom && selectedName && startDate && isValidDate(startDate)) {
            const date = new Date(startDate)
            selectedName = `${date.getUTCFullYear()} ${selectedName}`
          }

          onChange({name: selectedName})
        }}
      />
      {dateRowClassName
        ? <div className={dateRowClassName}>{dateFields}</div>
        : dateFields
      }
    </>
  )
}
